import type { Event, EventVideoConference, EventWebinar } from 'models';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePermissions } from 'permissions';
import {
  CloseCircleLineIcon,
  CopyLineIcon,
  Flag1LineIcon,
  type MenuProps,
  type ModalOptions,
  PencilLineIcon,
} from 'ui';
import { DuplicateEventModal, type DuplicateEventModalOptions } from '../../../duplicate-event-modal';
import { EditEventModal, type EditEventModalOptions } from '../../../edit-event-modal';
import { useCancelEvent, useReportEvent } from '../../../hooks';

type P = {
  navigateToEventDetail: (id: string) => Promise<void>;
};

export const useSocialEventDropdownActions = ({ navigateToEventDetail }: P) => {
  const { t } = useTranslation();
  const { canCancelSocialEvent, canDuplicateSocialEvent, canEditSocialEvent, canReportSocialEvent } = usePermissions();
  const [editEventOptions, setEditEventOptions] = useState<ModalOptions<EditEventModalOptions>>({ isOpened: false });
  const [duplicateEventOptions, setDuplicateEventOptions] = useState<ModalOptions<DuplicateEventModalOptions>>({
    isOpened: false,
  });

  const { confirm: cancelEvent, contextHolder: contextHolderCancel } = useCancelEvent();
  const { confirm: reportEvent, contextHolder: contextHolderReport } = useReportEvent();

  const closeEditEventModal = useCallback(() => {
    setEditEventOptions({ isOpened: false });
  }, []);

  const closeDuplicateEventModal = useCallback(() => {
    setDuplicateEventOptions({ isOpened: false });
  }, []);

  const onDuplicateEventSuccess = useCallback(
    async (eventData: Event) => {
      closeDuplicateEventModal();
      await navigateToEventDetail(eventData.id);
    },
    [closeDuplicateEventModal, navigateToEventDetail],
  );

  const getActions = useCallback(
    ({ event }: { event: EventVideoConference | EventWebinar }) => {
      const actions: MenuProps['items'] = [];

      if (canEditSocialEvent({ event })) {
        actions.push({
          icon: <PencilLineIcon />,
          key: 'edit',
          label: t('Edit'),
          onClick() {
            setEditEventOptions({ event, isOpened: true });
          },
        });
      }

      if (canReportSocialEvent({ event })) {
        actions.push({
          icon: <Flag1LineIcon />,
          key: 'report',
          label: t('Report'),
          onClick() {
            reportEvent({ event });
          },
        });
      }

      if (canDuplicateSocialEvent({ event })) {
        actions.push({
          icon: <CopyLineIcon />,
          key: 'duplicate',
          label: t('Duplicate'),
          onClick() {
            setDuplicateEventOptions({ event, isOpened: true });
          },
        });
      }

      if (canCancelSocialEvent({ event })) {
        actions.push({
          danger: true,
          icon: <CloseCircleLineIcon />,
          key: 'cancel',
          label: t('Cancel'),
          onClick() {
            cancelEvent({ event });
          },
        });
      }

      return actions;
    },
    [
      canCancelSocialEvent,
      canDuplicateSocialEvent,
      canEditSocialEvent,
      canReportSocialEvent,
      cancelEvent,
      reportEvent,
      t,
    ],
  );

  return useMemo(
    () => ({
      contextHolders: (
        <>
          {contextHolderCancel}
          {contextHolderReport}
          <EditEventModal onCancel={closeEditEventModal} onSuccess={closeEditEventModal} options={editEventOptions} />
          <DuplicateEventModal
            onCancel={closeDuplicateEventModal}
            onSuccess={onDuplicateEventSuccess}
            options={duplicateEventOptions}
          />
        </>
      ),
      getActions,
    }),
    [
      contextHolderCancel,
      contextHolderReport,
      closeEditEventModal,
      closeDuplicateEventModal,
      onDuplicateEventSuccess,
      editEventOptions,
      duplicateEventOptions,
      getActions,
    ],
  );
};
